import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { PRICING_MODELS } from 'consts/listing';
import { clientApiPatch } from 'lib/api_helper';
import { Listing, SellerLevel } from 'types';

import { useSellerProfile } from './sellerProfile';

type ListingContextInterface = {
  listing: Listing;
  updateListing: (newFields: object) => Promise<Listing>;
  refreshListing: (listing: Listing) => void;
  sellerLevels: SellerLevel[];
  isAuction: boolean;
};
export const ListingContext = createContext<ListingContextInterface>({} as ListingContextInterface);

type ListingContextProviderProps = PropsWithChildren & {
  initialListing: Listing;
  sellerLevels: SellerLevel[];
};

export const ListingContextProvider: React.FC<ListingContextProviderProps> = ({
  children,
  initialListing,
  sellerLevels
}) => {
  const [listing, setListing] = useState<Listing>(initialListing);
  const sellerProfile = useSellerProfile();

  useEffect(() => {
    setListing(initialListing);
  }, [initialListing]);

  const updateListing = useCallback(
    async (newFields: object) =>
      new Promise(resolve => {
        if (sellerProfile?.id === listing.sellerProfile.id) {
          return clientApiPatch<Listing, object>('/api/listings/seller', { ...newFields, listingId: listing.id }).then(
            () => {
              const updatedListing: Listing = { ...listing, ...newFields, model: listing.model };

              setListing(updatedListing);
              resolve(updatedListing);
            }
          );
        }

        return Promise.resolve(listing);
      }),
    [sellerProfile, listing]
  );

  const refreshListing = useCallback((newListing: Listing) => setListing(newListing), [setListing]);

  const value = useMemo(
    () => ({
      listing,
      updateListing,
      sellerLevels,
      refreshListing,
      isAuction: listing.activePricingModel === PRICING_MODELS.auction
    }),
    [listing, updateListing, sellerLevels, refreshListing]
  ) as ListingContextInterface;

  return <ListingContext.Provider value={value}>{children}</ListingContext.Provider>;
};

export const useListing = () => useContext(ListingContext);
